/**
 * Mapbox is the app's only mapping stack: GL JS renders the maps, and the
 * Geocoding API does address search and reverse lookup. Nothing here talks to
 * Google.
 *
 * The token is a public (`pk.`) token — it ships in the bundle by necessity, so
 * restrict it by URL in the Mapbox account rather than treating it as a secret.
 */
export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

/** Where the map starts when we know nothing else: central Dhaka. */
export const DEFAULT_CENTER = { lat: 23.777176, lng: 90.407608 };

export const MAPBOX_STYLE =
  process.env.REACT_APP_MAPBOX_STYLE || "mapbox://styles/mapbox/streets-v12";

const GEOCODE_BASE = "https://api.mapbox.com/geocoding/v5/mapbox.places";

/** Mapbox rejects unencoded `;` and `?` in the query path. */
const cleanQuery = (q) => encodeURIComponent(String(q).replace(/[;?]/g, " ").trim());

const assertToken = () => {
  if (!MAPBOX_TOKEN) {
    throw new Error(
      "Mapbox token is missing — set REACT_APP_MAPBOX_TOKEN in your .env",
    );
  }
};

/**
 * Pulls the parts the app stores alongside a chosen location. Mapbox returns
 * these as `context` entries keyed by a type prefix.
 */
const contextOf = (feature, type) =>
  feature?.context?.find((c) => c.id?.startsWith(`${type}.`))?.text || "";

const toPlace = (feature) => ({
  id: feature.id,
  // `place_name` is the full comma-separated address.
  address: feature.place_name,
  // Mapbox is lng,lat — everything in this app is lat,lng.
  lat: feature.center[1],
  lng: feature.center[0],
  area: contextOf(feature, "neighborhood") || contextOf(feature, "locality"),
  city: contextOf(feature, "place"),
  state: contextOf(feature, "region"),
  country: contextOf(feature, "country"),
});

/**
 * Address search for the location pickers.
 *
 * Unlike the Google Places autocomplete this replaces, each suggestion already
 * carries its coordinates — so selecting one needs no second lookup.
 *
 * @param {string} query
 * @param {{ proximity?: {lat,lng}, limit?: number, signal?: AbortSignal }} [options]
 * @returns {Promise<Array<{id,address,lat,lng,area,city,state,country}>>}
 */
export const searchPlaces = async (query, { proximity, limit = 6, signal } = {}) => {
  const term = String(query || "").trim();
  if (term.length < 2) return [];
  assertToken();

  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    // The customer app serves Bangladesh only — same restriction the Google
    // autocomplete had.
    country: "bd",
    language: "en",
    limit: String(limit),
    types: "address,poi,place,locality,neighborhood",
  });
  // Bias results toward where the customer already is.
  if (proximity) params.set("proximity", `${proximity.lng},${proximity.lat}`);

  const res = await fetch(`${GEOCODE_BASE}/${cleanQuery(term)}.json?${params}`, {
    signal,
  });
  if (!res.ok) throw new Error("Address search is unavailable right now.");

  const body = await res.json();
  return (body.features || []).map(toPlace);
};

/**
 * Coordinates → address, for "locate me" and for dragging the map pin.
 * @returns {Promise<{address,lat,lng,area,city,state,country}|null>}
 */
export const reverseGeocode = async (lat, lng, { signal } = {}) => {
  assertToken();

  // No `limit` here on purpose: Mapbox rejects it on reverse geocoding when
  // several `types` are requested, and the default of 1 is what we want anyway.
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    language: "en",
    types: "address,poi,place,locality,neighborhood",
  });

  const res = await fetch(`${GEOCODE_BASE}/${lng},${lat}.json?${params}`, { signal });
  if (!res.ok) throw new Error("Could not look up that location.");

  const body = await res.json();
  const feature = body.features?.[0];
  if (!feature) return null;

  // Keep the caller's exact coordinates: the matched feature's centre can sit
  // a few metres away, and the pin should stay where it was put.
  return { ...toPlace(feature), lat, lng };
};
